import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/apiClient";
import {
  Store, Users, Eye, Film, ArrowRight, RefreshCw, Crown,
  ChevronDown, ChevronUp, AlertCircle, Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../components/shared/Header";
import Footer from "../components/Footer";
import RecentSalesTicker from "../components/marketplace/RecentSalesTicker";
import MarketplaceVideoOffers from "../components/marketplace/MarketplaceVideoOffers";
import MarketplaceAppOnlyGate from "../components/MarketplaceAppOnlyGate";
import { IS_NATIVE } from "../lib/platform";

// (Header is rendered globally in App.js — do NOT add it here, it duplicates.)

// Public marketplace for tradeable sub-channels.
// Anonymity-safe by design: every listing exposes only the channel handle +
// vanity stats (videos, followers, loops). Seller identity is never surfaced.
//
// 2026-05-14 — gated to the native iOS / Android app only. Web visitors at
// `/marketplace` see <MarketplaceAppOnlyGate /> instead. See
// `frontend/src/lib/platform.js` for the detection helper and
// `components/MarketplaceAppOnlyGate.jsx` for the install-prompt landing.
//
// Hooks-order safety: the gate decision happens in the EXPORTED wrapper
// below, not inside MarketplaceNativePage — so neither branch ever
// renders a different number of hooks across renders.
const MarketplaceNativePage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [buying, setBuying] = useState(null); // listing.id during buy attempt

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/marketplace/listings?sort=${sort}&limit=50`);
      setItems(data.items || []);
    } catch (err) {
      toast.error("Couldn't load the marketplace");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

  const handleBuy = async (lst) => {
    if (!user) {
      toast.error("Sign in to buy a channel");
      return;
    }
    if ((user.subscription_tier || "free").toLowerCase() !== "producer") {
      toast.error("Only Producer-tier subscribers can buy channels.");
      navigate("/pricing");
      return;
    }
    const confirmed = window.confirm(
      `Buy /${lst.channel_username} for ${lst.price_credits} credits?\n\n` +
      `You'll inherit:\n` +
      `• ${lst.video_count} video(s)\n` +
      `• ${lst.follower_count} follower(s)\n` +
      `• ${lst.total_loops} total loops\n\n` +
      `This is final — no refunds.`
    );
    if (!confirmed) return;
    setBuying(lst.id);
    try {
      const res = await api.post(`/marketplace/listings/${lst.id}/buy`, {});
      toast.success(`/${res.data.channel_username} is now yours!`);
      await refreshUser?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    }
    setBuying(null);
  };

  const isProducer = (user?.subscription_tier || "free").toLowerCase() === "producer";

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <main className="flex-1 max-w-6xl mx-auto px-4 py-8 pb-32 sm:pb-8 w-full">
        <RecentSalesTicker />
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
              <Store className="h-8 w-8 text-emerald-400" />
              Marketplace
            </h1>
            <p className="text-gray-400 mt-2 max-w-2xl text-sm">
              Buy and sell <strong className="text-emerald-300">channels</strong> and{" "}
              <strong className="text-emerald-300">videos</strong> anonymously. Channels come
              with their handle, video library, and follower base. Video offers stay private —
              the producer never sees who's bidding.
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="border-gray-700 text-gray-300 hover:bg-gray-800 text-sm"
            data-testid="marketplace-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Expired Marketplace entry — second-life liquidations are
            distinct from the auction-style main marketplace, so we
            surface them with a separate banner instead of mixing rows. */}
        <Link
          to="/marketplace/expired"
          className="block mb-5 p-4 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-amber-500/0 hover:bg-amber-500/15 transition-colors"
          data-testid="expired-market-entry"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-amber-200 font-semibold flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" /> Expired Marketplace — videos for spare change
              </h2>
              <p className="text-amber-200/80 text-xs mt-0.5 max-w-xl">
                Clips whose creators let them lapse. Buy at the floor price, claim a
                fresh 30 days, make them yours. Producers get nothing — pure liquidation.
              </p>
            </div>
            <span className="text-amber-200 text-xs underline">Browse →</span>
          </div>
        </Link>

        {/* Sort + producer perks banner */}
        <div className="flex flex-wrap items-center gap-2 mb-5 text-sm">
          <span className="text-gray-500 text-xs uppercase tracking-wide mr-1">Sort:</span>
          {[
            { k: "recent", l: "Newest" },
            { k: "price_asc", l: "Cheapest" },
            { k: "price_desc", l: "Most Expensive" },
            { k: "followers", l: "Most Followers" },
          ].map(({ k, l }) => (
            <button
              key={k}
              type="button"
              onClick={() => setSort(k)}
              className={`px-3 py-1 rounded-full text-xs border ${
                sort === k
                  ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                  : "bg-gray-900 border-gray-700 text-gray-400 hover:border-gray-600"
              }`}
              data-testid={`marketplace-sort-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {!user && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 mb-5 text-sm text-gray-400 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
            <div>
              <strong className="text-amber-200">Sign in to purchase.</strong>{" "}
              Buying a channel requires a Producer subscription.{" "}
              <Link to="/pricing" className="underline text-emerald-400">Pricing →</Link>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          // Soft-gate the empty state. While we're pre-launch and the
          // channel market is still being seeded, lean on this real estate
          // to sell Producer subscriptions instead of showing dead space.
          // Producer-tier users already inside the market see a normal
          // empty state with a link to their dashboard.
          isProducer ? (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
              <Store className="h-10 w-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-300 text-lg">The market is quiet right now.</p>
              <p className="text-gray-500 text-sm mt-1">Check back soon, or list one of your own channels.</p>
              <Link to="/dashboard">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-sm" data-testid="marketplace-go-dashboard">
                  Go to my channels →
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Channels-for-sale producer gate */}
              <div className="bg-gradient-to-br from-amber-950/50 to-gray-900 border border-amber-500/30 rounded-lg p-6" data-testid="marketplace-channels-gate">
                <div className="flex items-start gap-3">
                  <Crown className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-amber-200 font-semibold text-lg">Channels For Sale</h3>
                    <p className="text-gray-400 text-sm mt-1 leading-relaxed">
                      Want to see channels for sale, or sell your own?{" "}
                      <strong className="text-amber-200">Become a Producer</strong> to access the
                      channel market — buy ready-made audiences, or cash out your own brand without
                      ever revealing your identity.
                    </p>
                    <Link to="/pricing">
                      <Button className="mt-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm" data-testid="marketplace-become-producer-channels">
                        Become a Producer →
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Videos-for-sale producer gate */}
              <div className="bg-gradient-to-br from-emerald-950/40 to-gray-900 border border-emerald-500/30 rounded-lg p-6" data-testid="marketplace-videos-gate">
                <div className="flex items-start gap-3">
                  <Film className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-emerald-200 font-semibold text-lg">Make an Offer on a Video</h3>
                    <p className="text-gray-400 text-sm mt-1 leading-relaxed">
                      See a video you'd love to own outright? Producers can make{" "}
                      <strong className="text-emerald-200">anonymous offers</strong> on any
                      published video. The producer of that video sees the price — never your
                      identity. If they accept, ownership flips to you and the original producer
                      gets credited forever as the creator.
                    </p>
                    <Link to="/pricing">
                      <Button className="mt-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm" data-testid="marketplace-become-producer-videos">
                        Unlock with Producer →
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketplace-grid">
            {items.map((lst) => (
              <Card
                key={lst.id}
                className="bg-gray-900 border-gray-800 hover:border-emerald-500/40 transition-colors"
                data-testid={`listing-${lst.channel_username}`}
              >
                <CardContent className="p-4">
                  <Link
                    to={`/${lst.channel_username}`}
                    className="block font-mono text-emerald-400 text-lg hover:text-emerald-300 truncate"
                    data-testid={`listing-handle-${lst.channel_username}`}
                  >
                    /{lst.channel_username}
                  </Link>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1" title="videos">
                      <Film className="h-3 w-3" /> {lst.video_count}
                    </span>
                    <span className="flex items-center gap-1" title="followers">
                      <Users className="h-3 w-3" /> {lst.follower_count}
                    </span>
                    <span className="flex items-center gap-1" title="total loops">
                      <Eye className="h-3 w-3" /> {lst.total_loops.toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500">Price</p>
                      <p className="text-xl font-bold text-white">
                        {lst.price_credits.toLocaleString()}
                        <span className="text-gray-400 text-xs font-normal ml-1">cr</span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleBuy(lst)}
                      disabled={buying === lst.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                      data-testid={`buy-${lst.channel_username}`}
                    >
                      {buying === lst.id ? "Buying..." : <>Buy <ArrowRight className="h-3 w-3 ml-1" /></>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Producer-only "Make an Offer on a Video" panel. Shown below
            the Channels grid (or below the empty state) so Producers
            have a clear, secondary action even when no channels are
            for sale. Non-Producers continue to see the gold gate panel
            up above — no double-rendering. */}
        {isProducer && <MarketplaceVideoOffers />}

        {/* Seller-side education footer */}
        <div className="mt-10 bg-gray-900/50 border border-gray-800 rounded-lg p-4 text-sm">
          <p className="text-amber-200 font-medium mb-1 flex items-center gap-2">
            <Crown className="h-4 w-4" /> Selling your own channels
          </p>
          <p className="text-gray-400 text-xs leading-relaxed">
            Producer-tier users can list any sub-channel they've owned for at least 7 days.
            GlobalEyes takes a <strong className="text-gray-200">15% platform fee</strong> on each sale;
            you keep the rest. Your master account stays anonymous — buyers see only the channel.
            {" "}
            <Link to="/dashboard" className="underline text-emerald-400">Manage your channels →</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Exported wrapper — picks the right component at module load, before
// any hooks run, so the React hooks-order invariant is preserved.
const MarketplacePage = IS_NATIVE ? MarketplaceNativePage : MarketplaceAppOnlyGate;

export default MarketplacePage;
